import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function AdminLogin() {
  const { login, isAuthenticated, checking } = useAdminAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checking && isAuthenticated) {
      navigate("/admin", { replace: true });
    }
  }, [checking, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4 relative overflow-hidden">
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full pointer-events-none opacity-70"
        style={{ background: "var(--gradient-radial-glow)" }}
        aria-hidden="true"
      />
      <div className="noise-overlay" aria-hidden="true" />

      <div className="relative w-full max-w-md card-lux rounded-2xl p-8 sm:p-10">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/images/jewellery/logo.png"
            alt="Vini Gems And Jewellers"
            className="h-16 w-16 rounded-full ring-1 ring-primary/40 mb-4 object-contain"
          />
          <h1 className="font-heading text-2xl font-semibold text-foreground">Admin Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">Vini Gems And Jewellers</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
            />
          </div>
          <Button type="submit" variant="premium" size="lg" className="w-full" disabled={loading} data-testid="admin-login-submit">
            <Lock className="h-4 w-4" /> {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Authorized personnel only. Contact the site owner if you need access.
        </p>
      </div>
    </div>
  );
}
